import React, { useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import usePurchaseAdd from '../../hooks/usePurchaseAdd';
import { useSession } from '../../contexts/SessionContext';
import { Purchase, PurchaseType } from '../../interfaces';
import useFetchPurchaseTypes from '../../hooks/useFetchPurchaseTypes';
import SearchableDropdown from '../common/SearchableDropDown';
import useSearchEan from '../../hooks/useSearchEan';

interface PurchaseAddControlProps {
  show: boolean;
  handleClose: () => void;
  ean: string;
  onSuccess: () => void;
}

interface FilterCriteria {
  ean: string;
}

const PurchaseAddModal: React.FC<PurchaseAddControlProps> = ({ show, ean, handleClose, onSuccess }) => {
  const { dispatch } = useSession();
  const { t } = useTranslation();
  const { purchaseAdd, error: submitError } = usePurchaseAdd();
  const { apiResponse: purchaseTypes, error: fetchError } = useFetchPurchaseTypes();
  const [filterCriteria, setFilterCriteria] = useState<FilterCriteria>({ ean: '' });
  const { searchEan } = useSearchEan(filterCriteria);
  const [typeIsSpare, setTypeIsSpare] = useState(false);

  const [purchaseData, setPurchaseData] = useState<Purchase>({
    id: 0,
    device: {
      id: 0,
      purchases: [],
      services: [],
      updatedAt: new Date(),
      createdAt: new Date(),
      lastApiCall: new Date(),
      dns: { id: 0, internalIP: '', ip: '' },
      ean: ean,
      crmInfo: {
        isInCrm: false,
        product: { description: '', name: '', link: '' },
        customer: { address: '', zip: '', town: '', clientCode: '', link: '', name: '' },
        isShipped: false,
        shipment: { link: '', name: '' }
      }
    },
    targetDevice: {
      id: 0,
      ean: ean,
      purchases: [],
      services: [],
      updatedAt: new Date(),
      createdAt: new Date(),
      lastApiCall: new Date(),
      dns: { id: 0, internalIP: '', ip: '' },
      crmInfo: {
        isInCrm: false,
        product: { description: '', name: '', link: '' },
        customer: { address: '', zip: '', town: '', clientCode: '', link: '', name: '' },
        isShipped: false,
        shipment: { link: '', name: '' }
      }
    },
    hash: generateGUID(),
    purchaseDate: new Date(),
    activationDate: new Date(),
    type: { id: 0, name: '' }
  });

  const [isPurchaseTypeSelected, setIsPurchaseTypeSelected] = useState(false);

  const handleSearchByEan = async (searchTerm: string) => {
    const devices = await searchEan(searchTerm);
    return devices.map((device: any) => ({
      id: device.id,
      label: device.ean,
    }));
  };

  const handleChange = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    if (name === 'type') {
      const selectedType = purchaseTypes.find(type => type.id === parseInt(value as string));
      if (selectedType) {
        setPurchaseData(prevState => ({
          ...prevState,
          type: selectedType
        }));
        if (selectedType.name === "Spare") {
          setTypeIsSpare(true);
          setPurchaseData(prevState => ({
            ...prevState,
            targetDevice: {
              ...prevState.targetDevice,
              ean: ean
            }
          }))
        } else {
          setTypeIsSpare(false);
          setPurchaseData(prevState => ({
            ...prevState,
            targetDevice: {
              ...prevState.targetDevice,
              ean: ""
            }
          }));
        }
        setIsPurchaseTypeSelected(true);
      } else {
        setIsPurchaseTypeSelected(false);
      }
    } else {
      setPurchaseData(prevState => ({
        ...prevState,
        [name as string]: value,
      }));
    }
  };

  const handleTargetEANChange = (name: string) => {
    setPurchaseData(prevState => ({
      ...prevState,
      targetDevice: {
        id: 0,
        purchases: [],
        services: [],
        updatedAt: new Date(),
        createdAt: new Date(),
        lastApiCall: new Date(),
        dns: { id: 0, internalIP: '', ip: '' },
        ean: name,
        crmInfo: {
          isInCrm: false,
          product: { description: '', name: '', link: '' },
          customer: { address: '', zip: '', town: '', clientCode: '', link: '', name: '' },
          isShipped: false,
          shipment: { link: '', name: '' }
        }
      }
    }));
  };

  function generateGUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & (0x3 | 0x8));
      return v.toString(16);
    });
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const data = await purchaseAdd(purchaseData);
    if (data) {
      dispatch({ type: 'PURCHASE_ADD', payload: { purchase: data } });
      handleClose();
      onSuccess();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size='lg'>
      <Modal.Header closeButton>
        <Modal.Title className='text-secondary text-center fs-2 fw-semibold position-relative top-0 start-50 translate-middle-x'>{t('devices.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          {submitError && <Alert variant="danger">{submitError}</Alert>}
          {fetchError && <Alert variant="danger">{fetchError}</Alert>}
          <Row className="g-4">
            <Col md={12}>
              <Form.Group controlId="formPurchaseType">
                <Form.Label className="fs-4 fw-medium text-secondary">{t("type")}</Form.Label>
                <Form.Control as="select" name="type" value={purchaseData.type.id} onChange={handleChange} className='text-secondary bg-light fw-medium'>
                  <option value="">{t("devices.modal.label.selectPurchaseType")}</option>
                  {purchaseTypes.map((type: PurchaseType) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group controlId="formField1">
                <Form.Label className="fs-4 fw-medium text-secondary">{t("devices.modal.label.hash")}</Form.Label>
                <Form.Control type="text" name="hash" value={purchaseData.hash} onChange={handleChange} className='text-secondary bg-light fw-medium' placeholder={t("devices.purchases.enterHash")} />
              </Form.Group>
            </Col>
          </Row>
          {typeIsSpare &&
            <SearchableDropdown
              controlId="formField1"
              label="devices.purchases.targetEAN"
              textplaceholder="devices.purchases.targetEAN"
              dropdownplaceholder="devices.filter.enterEAN"
              handleSetValue={handleTargetEANChange}
              searchMethod={handleSearchByEan}
              defaultValue={ean}
            />
          }
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" className='px-2 fw-medium' onClick={handleSubmit} disabled={!isPurchaseTypeSelected}>
          {t("devices.modal.label.submit")}
        </Button>
        <Button variant="primary" className='px-2 fw-medium' onClick={handleClose}>
          {t("close")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PurchaseAddModal;
